import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { BroadcastDryRunResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

interface DryRunResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: BroadcastDryRunResponse | null;
}

/**
 * Dialog component to display broadcast dry run results with a recipient table
 */
export function DryRunResultsDialog({
  open,
  onOpenChange,
  results,
}: DryRunResultsDialogProps) {
  const users = results?.users ?? [];
  const orgs = results?.orgs ?? [];
  const hasRecipients = users.length > 0 || orgs.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Dry Run Results</DialogTitle>
          <DialogDescription>
            Preview of recipients who will receive this broadcast
          </DialogDescription>
        </DialogHeader>

        <div className="max-w-fit flex items-center gap-4 py-2">
          <Badge variant="secondary" className="text-sm">
            {results?.totalRecipients ?? 0} recipient(s)
          </Badge>
          {users.length > 0 && (
            <Badge variant="outline" className="text-sm">
              {users.length} user(s)
            </Badge>
          )}
          {orgs.length > 0 && (
            <Badge variant="outline" className="text-sm">
              {orgs.length} org(s)
            </Badge>
          )}
        </div>

        {!hasRecipients ? (
          <div className="text-center text-muted-foreground py-8 border border-dashed rounded-lg">
            No recipients match the current filter criteria.
          </div>
        ) : (
          <div className="flex-1 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <Badge variant="secondary">User</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {user.userId}
                    </TableCell>
                  </TableRow>
                ))}
                {orgs.map((org) => (
                  <TableRow key={org.orgId}>
                    <TableCell>
                      <Badge variant="outline">Org</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {org.orgId}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
